/**
 * Blog Service
 * Handles all blog-related Firestore operations
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';

import { db } from './config';
import type { BlogPost, PageStatus } from '@shared/types/cms';
import type { ContentBlock } from '@shared/types/blocks';

/**
 * Get all blog posts
 */
export async function getBlogPosts(): Promise<BlogPost[]> {
  const snapshot = await getDocs(
    query(collection(db, 'blog_posts'), orderBy('createdAt', 'desc'))
  );

  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: (data.createdAt as Timestamp).toDate(),
      updatedAt: (data.updatedAt as Timestamp).toDate(),
      publishedAt: data.publishedAt ? (data.publishedAt as Timestamp).toDate() : undefined,
    } as BlogPost;
  });
}

/**
 * Get a single blog post by ID
 */
export async function getBlogPost(postId: string): Promise<BlogPost | null> {
  const docRef = doc(db, 'blog_posts', postId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    createdAt: (data.createdAt as Timestamp).toDate(),
    updatedAt: (data.updatedAt as Timestamp).toDate(),
    publishedAt: data.publishedAt ? (data.publishedAt as Timestamp).toDate() : undefined,
  } as BlogPost;
}

/**
 * Get blog post by slug
 */
export async function getBlogPostBySlug(slug: string): Promise<BlogPost | null> {
  const snapshot = await getDocs(
    query(collection(db, 'blog_posts'), where('slug', '==', slug), limit(1))
  );

  if (snapshot.empty || snapshot.docs.length === 0) {
    return null;
  }

  const docSnap = snapshot.docs[0];
  if (!docSnap) {
    return null;
  }

  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    createdAt: (data.createdAt as Timestamp).toDate(),
    updatedAt: (data.updatedAt as Timestamp).toDate(),
    publishedAt: data.publishedAt ? (data.publishedAt as Timestamp).toDate() : undefined,
  } as BlogPost;
}

/**
 * Create a new blog post
 */
export async function createBlogPost(postData: {
  title: string;
  slug: string;
  excerpt?: string;
  blocks: ContentBlock[];
  featuredImage?: string;
  authorId: string;
  authorName?: string;
  category?: string;
  tags?: string[];
  status: PageStatus;
  settings: {
    seo: {
      metaTitle: string;
      metaDescription: string;
      ogImage?: string;
      keywords?: string[];
    };
    customFonts?: {
      heading?: string;
      body?: string;
      cta?: string;
    };
  };
}): Promise<string> {
  const docRef = await addDoc(collection(db, 'blog_posts'), {
    ...postData,
    publishedAt: postData.status === 'published' ? serverTimestamp() : null,
    viewCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}

/**
 * Update a blog post
 */
export async function updateBlogPost(
  postId: string,
  updates: Partial<{
    title: string;
    slug: string;
    excerpt?: string;
    blocks: ContentBlock[];
    featuredImage?: string;
    category?: string;
    tags?: string[];
    status: PageStatus;
    settings: {
      seo: {
        metaTitle: string;
        metaDescription: string;
        ogImage?: string;
        keywords?: string[];
      };
      customFonts?: {
        heading?: string;
        body?: string;
        cta?: string;
      };
    };
  }>
): Promise<void> {
  const docRef = doc(db, 'blog_posts', postId);
  const currentDoc = await getDoc(docRef);
  const currentData = currentDoc.data();

  const updateData: any = {
    ...updates,
    updatedAt: serverTimestamp(),
  };

  // If status changed to published and wasn't published before, set publishedAt
  if (updates.status === 'published' && currentData?.status !== 'published') {
    updateData.publishedAt = serverTimestamp();
  }

  // If status changed from published, don't update publishedAt
  if (updates.status && updates.status !== 'published' && currentData?.status === 'published') {
    // Keep existing publishedAt
    delete updateData.publishedAt;
  }

  await updateDoc(docRef, updateData);
}

/**
 * Delete a blog post
 */
export async function deleteBlogPost(postId: string): Promise<void> {
  await deleteDoc(doc(db, 'blog_posts', postId));
}

/**
 * Increment view count
 */
export async function incrementBlogPostViews(postId: string): Promise<void> {
  const docRef = doc(db, 'blog_posts', postId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const currentViews = docSnap.data().viewCount || 0;
    await updateDoc(docRef, {
      viewCount: currentViews + 1,
      updatedAt: serverTimestamp(),
    });
  }
}

